import {
	createRsocketClient as createBettingServiceRsocketClient,
	getRsocketClient,
	Subscription
} from './betting-service/rsocket-client';
import {
	createBettingServiceApiClient,
	getBettingServiceApiClient
} from '~/components/sportsbook/lobby/api/betting-service/betting-service-api-client';
import {
	BetsResponse,
	CalculateCashoutResponse,
	CheckBetsRequest,
	PlaceBetsRequest,
	PlaceBetsResponse,
	SubmitCashoutRequest,
	SubmitCashoutResponse,
	UpdateAcceptModeRequest
} from '~/components/sportsbook/lobby/api/betting-service/betting-service-types';
import { BetStatus, OddsFormat } from '~/components/sportsbook/lobby/sportsbook-types';
import { BET_PATH, BETS_PATH } from '~/components/sportsbook/lobby/api/betting-service/betting-service-constants';

type SetupParams = {
	franchiseCode: string;
	locale: string;
};

const ROUTE_SUBSCRIBE_TO_DELAYED_BETS = `${BET_PATH}/subscribeToDelayedBets`;

const ROUTE_SUBSCRIBE_TO_BET_UPDATES = `${BETS_PATH}/subscribeToBetUpdates`;

const ROUTE_SUBSCRIBE_TO_OPEN_BETS_COUNT = `${BETS_PATH}/subscribeToOpenBetsCount`;

export const setUpBettingServiceApi = (params: SetupParams) => {
	const { franchiseCode, locale } = params;
	createBettingServiceApiClient(franchiseCode);

	const websocketHost = window.location.host;
	createBettingServiceRsocketClient(
		`wss://${websocketHost}/sportsbook-betting-service/public/rsocket?franchiseCode=${franchiseCode}&locale=${locale}`
	);
};

export const refresh = async (franchiseCode: string, uuid: string) =>
	getBettingServiceApiClient().refresh(franchiseCode, uuid);
export const checkBets = async (req: CheckBetsRequest, abortSignal: AbortSignal) =>
	getBettingServiceApiClient().checkBets(req, abortSignal);
export const placeBets = async (req: PlaceBetsRequest) => getBettingServiceApiClient().placeBets(req);
export const updateAcceptMode = async (req: UpdateAcceptModeRequest) =>
	getBettingServiceApiClient().updateAcceptMode(req);
export const submitCashout = async (req: SubmitCashoutRequest) => getBettingServiceApiClient().submitCashout(req);
export const generateBetBuilderUrl = async (betBuilderId: string, oddsFormat: OddsFormat, hasReversedOrder: boolean) =>
	getBettingServiceApiClient().generateBetBuilderUrl(betBuilderId, oddsFormat, hasReversedOrder);
export const getBetBuilderItem = async (betId: string) => getBettingServiceApiClient().getBetBuilderItem(betId);
export const fetchBets = async (
	betStatus: BetStatus,
	startIndex: number,
	pageSize: number,
	startDate?: string,
	endDate?: string
) => getBettingServiceApiClient().fetchBets(betStatus, startIndex, pageSize, startDate, endDate);
export const fetchBet = async (betId: number) => getBettingServiceApiClient().fetchBet(betId);
export const fetchAcceptMode = async () => getBettingServiceApiClient().fetchAcceptMode();
export const listActiveBonuses = async () => getBettingServiceApiClient().listActiveBonuses();
export const subscribeToPlacementUpdates = (subscription: Subscription<PlaceBetsResponse>) =>
	getRsocketClient().subscribe(ROUTE_SUBSCRIBE_TO_DELAYED_BETS, subscription);
export const unsubscribeFromPlacementUpdates = (ref: string) => getRsocketClient().unsubscribe(ref);

export const disposeSubscriptionToCashoutInformation = (ref: string) => {
	getRsocketClient().unsubscribe(ref);
};

export const createSubscriptionToCashoutInformation = (
	ref: string,
	onMessage: (data: CalculateCashoutResponse[]) => void,
	onError: (error: unknown) => void
) => {
	disposeSubscriptionToCashoutInformation(ref);
	getRsocketClient().subscribe('/public/cashout/subscribeToCalculateAll', {
		ref,
		onMessage,
		onError,
		onSubscribed: () => {}
	});
};

export const disposeSubscriptionToCashoutSubmits = (ref: string) => {
	getRsocketClient().unsubscribe(ref);
};

export const createSubscriptionToCashoutSubmits = (ref: string, onMessage: (data: SubmitCashoutResponse) => void) => {
	disposeSubscriptionToCashoutSubmits(ref);
	getRsocketClient().subscribe('/public/cashout/subscribeToDelayedCashouts', {
		ref,
		onMessage,
		// eslint-disable-next-line no-console
		onError: console.warn,
		onSubscribed: () => {}
	});
};

export const disposeSubscriptionToBets = (ref: string) => {
	getRsocketClient().unsubscribe(ref);
};

export const createSubscriptionToBets = (
	ref: string,
	onMessage: (data: BetsResponse) => void,
	onError: (error: unknown) => void
) => {
	disposeSubscriptionToBets(ref);
	getRsocketClient().subscribe(ROUTE_SUBSCRIBE_TO_BET_UPDATES, {
		ref,
		onMessage,
		onError,
		onSubscribed: () => {}
	});
};

export const disposeSubscriptionToOpenBetsCount = (ref: string) => {
	getRsocketClient().unsubscribe(ref);
};

export const createSubscriptionToOpenBetsCount = (
	ref: string,
	onMessage: (data: { unsettledBetsCount: number }) => void,
	onError: (error: unknown) => void
) => {
	disposeSubscriptionToOpenBetsCount(ref);
	getRsocketClient().subscribe(ROUTE_SUBSCRIBE_TO_OPEN_BETS_COUNT, {
		ref,
		onMessage: (num: number) => onMessage({ unsettledBetsCount: num }),
		onError,
		onSubscribed: () => {}
	});
};

export const fetchOpenBetsCount = async () => getBettingServiceApiClient().fetchOpenBetsCount();
export const calculateCashOutAllOpenBets = async () => getBettingServiceApiClient().calculateCashOutAllOpenBets();

// Avoid propagating betting-service-client changes, they will replace their own singletons.
if (import.meta.webpackHot) {
	import.meta.webpackHot.accept(
		['./betting-service/betting-service-api-client.ts', './betting-service/rsocket-client.ts'],
		() => {}
	);
}
